import { Coins, CreditCard, Lock, MessageSquarePlus, Tag } from "lucide-react";
import type { CommercialTerms, TermsQuality } from "@/api/types";

// BUNDLE 1 STEP 5 — source + lifecycle-aware commercial-terms block (mockup v0.3).
// Renders the cC-mirror terms with per-mode visual treatment:
//   read_only (sync_1c) · editable (crm_managed draft/returned) · approver_only
//   (submitted) · locked (synced/approved). «Запит на зміну умов» is DISABLED —
//   the Action Engine is absent (TZ B3: hidden/disabled until ready).
// creditLimit is a permitted commercial term (E5); NO contract amount is shown.

const Q_BADGE: Partial<Record<TermsQuality, { label: string; cls: string }>> = {
  suspicious_outlier: { label: "перевірити", cls: "bg-warning text-warning-foreground" },
  non_standard: { label: "нестандартний", cls: "bg-muted text-muted-foreground" },
};

function QualityChip({ q }: { q: TermsQuality }) {
  const b = Q_BADGE[q];
  if (!b) return null;
  return <span className={`ml-1.5 rounded px-1 py-0.5 text-[10px] font-medium ${b.cls}`}>{b.label}</span>;
}

function fmtCredit(v: number | null, currency: string | null): string {
  if (v === null) return "—";
  const n = new Intl.NumberFormat("uk-UA").format(v);
  return `${n} ${currency || "₴"}`;
}

function Row({ icon, label, value, quality }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; quality?: TermsQuality;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</span>
      <span className="text-right text-foreground">
        {value}
        {quality && <QualityChip q={quality} />}
      </span>
    </div>
  );
}

const MODE_BADGE: Record<CommercialTerms["mode"], { label: string; cls: string; lock?: boolean }> = {
  read_only: { label: "з 1С · read-only", cls: "bg-muted text-muted-foreground", lock: true },
  editable: { label: "CRM draft · редагується", cls: "bg-primary/10 text-primary" },
  approver_only: { label: "На погодженні", cls: "bg-warning text-warning-foreground" },
  locked: { label: "locked", cls: "bg-muted text-muted-foreground", lock: true },
};

export function CommercialTermsBlock({
  terms,
  onChangeRequest,
}: {
  terms: CommercialTerms;
  /** G1-S3: when provided, «Запит на зміну умов» is enabled and opens the change
   *  request (the ONLY way to change sync_1c / locked terms). Absent ⇒ disabled
   *  with reason (no dead CTA, §0). */
  onChangeRequest?: () => void;
}) {
  const mb = MODE_BADGE[terms.mode];
  const priceLabel =
    terms.priceTypeName ??
    (terms.priceTypeQuality === "non_standard" ? "нестандартний тип" : "тип не задано");
  const showChangeRequest = terms.source === "sync_1c" || terms.mode === "locked";

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2 text-sm font-medium">
        <span className="flex items-center gap-2"><Coins className="h-4 w-4 text-primary" />Комерційні умови</span>
        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${mb.cls}`}>
          {mb.lock && <Lock className="h-3 w-3" />}{mb.label}
        </span>
      </div>
      <div className="space-y-2 p-4">
        {terms.hasTerms ? (
          <>
            {terms.reviewState === "returned_for_revision" && (
              <div className="mb-1 rounded-md bg-warning px-2 py-1 text-[11px] font-medium text-warning-foreground">
                на доопрацюванні
              </div>
            )}
            <Row icon={<CreditCard className="h-3.5 w-3.5 text-primary" />} label="Відстрочка"
                 value={terms.deferralDays !== null ? `${terms.deferralDays} дн.` : "—"}
                 quality={terms.deferralDaysQuality} />
            <Row icon={<Coins className="h-3.5 w-3.5 text-primary" />} label="Кредитний ліміт"
                 value={fmtCredit(terms.creditLimit, terms.currency)}
                 quality={terms.creditLimitQuality} />
            <Row icon={<Tag className="h-3.5 w-3.5 text-primary" />} label="Тип цін"
                 value={priceLabel} quality={terms.priceTypeQuality} />
            {terms.priceTypeCatalogMissing && terms.priceTypeName && (
              <div className="text-[11px] italic text-warning-foreground">тип цін: код у довіднику не знайдено — показано кешовану назву</div>
            )}
            {terms.mode === "editable" && (
              <div className="text-[11px] text-muted-foreground">* тип цін обов'язковий перед поданням (з BI-довідника, не вільний текст).</div>
            )}
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-[11px] text-muted-foreground">
                {terms.termsSyncedAt ? `оновлено ${terms.termsSyncedAt.slice(0, 10)}` : ""}
              </span>
              {showChangeRequest && (
                onChangeRequest ? (
                  <button
                    type="button"
                    onClick={onChangeRequest}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                  >
                    <MessageSquarePlus className="h-3 w-3" />Запит на зміну умов
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    title="Маршрут запиту не налаштовано"
                    className="inline-flex cursor-not-allowed items-center gap-1 text-[11px] text-muted-foreground/50"
                  >
                    <MessageSquarePlus className="h-3 w-3" />Запит на зміну умов
                  </button>
                )
              )}
            </div>
          </>
        ) : (
          <div className="text-xs italic text-muted-foreground">
            {terms.source === "sync_1c" ? "Немає даних у 1С" : "умови не задані"}
          </div>
        )}
      </div>
    </div>
  );
}
